import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTheme } from '../ThemeContext';

const F = "'Fira Code', monospace";

const Savings = ({ data, onUpdate, user }) => {
  const { T } = useTheme();
  const [saveAmount, setSaveAmount] = useState('');
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const listRef = useRef(null);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const handleAddSavings = async () => {
    const amt = parseFloat(saveAmount);
    if (!amt || amt <= 0) return showFlash('✗ ENTER VALID SAVINGS AMOUNT');
    setSaving(true);
    const entry = { amount: amt, note: saveNote.trim() || 'Savings', date: new Date().toISOString() };
    const entries = [...(data.savingsEntries || []), entry];
    const totalSavings = entries.reduce((s, e) => s + e.amount, 0);
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'savings'), { entries, totalSavings }, { merge: true });
      onUpdate({ savingsEntries: entries, totalSavings });
      setSaveAmount(''); setSaveNote('');
      showFlash('✓ SAVINGS DEPOSITED');
    } catch (e) { showFlash('✗ FIREBASE WRITE FAILED'); }
    setSaving(false);
  };

  const handleDeleteSavings = async (idx) => {
    const realIdx = (data.savingsEntries || []).length - 1 - idx;
    const entries = [...(data.savingsEntries || [])];
    if (!entries[realIdx]) return;
    entries.splice(realIdx, 1);
    const totalSavings = entries.reduce((s, e) => s + e.amount, 0);
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'savings'), { entries, totalSavings }, { merge: true });
      onUpdate({ savingsEntries: entries, totalSavings });
      showFlash('✓ SAVINGS RECORD DELETED');
    } catch (e) { showFlash('✗ DELETE FAILED'); }
    setDeleteConfirm(null);
  };

  const totalSavings = (data.savingsEntries || []).reduce((s, e) => s + e.amount, 0);
  const thisMonthSavings = (() => {
    const now = new Date();
    return (data.savingsEntries || []).filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).reduce((s, e) => s + e.amount, 0);
  })();

  const monthlyData = (() => {
    const map = {};
    (data.savingsEntries || []).forEach(e => {
      const key = new Date(e.date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
      map[key] = (map[key] || 0) + e.amount;
    });
    return Object.entries(map).slice(-8);
  })();
  const maxMonthly = Math.max(...monthlyData.map(m => m[1]), 1);

  const cumulativeData = (() => {
    const arr = []; let run = 0;
    (data.savingsEntries || []).forEach(e => { run += e.amount; arr.push(run); });
    return arr;
  })();

  const target = 1000000;
  const progress = Math.min((totalSavings / target) * 100, 100);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data.savingsEntries]);

  const cardS = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '22px 22px', marginBottom: 14, boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)', transition: 'background .3s,border .3s' };
  const hdrS  = { fontSize: 10, letterSpacing: '0.25em', color: T.text, fontFamily: F, marginBottom: 18, fontWeight: 700, paddingBottom: 10, borderBottom: `1px solid ${T.border}` };
  const statC = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', boxShadow: T.isDark ? 'none' : '0 2px 10px rgba(0,0,0,0.05)', transition: 'background .3s' };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      {flash && <Flash msg={flash} T={T} />}

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ ...statC, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: T.amber, opacity: 0.5 }} />
          <div style={{ fontSize: 7, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 10 }}>TOTAL SAVINGS</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.amber, fontFamily: F }}>₹{totalSavings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          <div style={{ width: '100%', height: 3, background: T.isDark ? T.muted : T.border, borderRadius: 2, marginTop: 12 }}>
            <div style={{ height: 3, borderRadius: 2, width: `${progress}%`, background: `linear-gradient(90deg, ${T.amber}, ${T.emerald})`, boxShadow: `0 0 8px ${T.amber}`, transition: 'width 0.6s ease' }} />
          </div>
          <div style={{ fontSize: 7, color: T.dim, marginTop: 5, letterSpacing: '0.15em' }}>{progress.toFixed(1)}% of ₹10,00,000 TARGET</div>
        </div>
        <SC label="THIS MONTH" value={`₹${thisMonthSavings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} color={T.emerald} T={T} />
        <SC label="DEPOSITS"   value={`${(data.savingsEntries || []).length}`}                                        color={T.cyan}    T={T} />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {monthlyData.length > 0 && (
          <div style={cardS}>
            <div style={hdrS}>📊 MONTHLY SAVINGS</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 100, padding: '0 4px' }}>
              {monthlyData.map(([month, val], i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 7, color: T.dim }}>₹{(val/1000).toFixed(0)}k</span>
                  <div style={{ width: '70%', maxWidth: 32, minHeight: 4, height: `${(val / maxMonthly) * 80}%`, background: `linear-gradient(180deg, ${T.amber}, ${T.amber}33)`, borderRadius: '3px 3px 0 0', transition: 'height 0.5s ease' }} />
                  <span style={{ fontSize: 7, color: T.dim }}>{month.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {cumulativeData.length >= 2 && (
          <div style={cardS}>
            <div style={hdrS}>📈 SAVINGS GROWTH</div>
            <div style={{ padding: '8px 0' }}>
              <SparkL data={cumulativeData} color={T.amber} w={280} h={90} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: T.dim, marginTop: 4 }}>
              <span>₹{cumulativeData[0]?.toLocaleString('en-IN')}</span>
              <span style={{ color: T.amber }}>₹{cumulativeData[cumulativeData.length - 1]?.toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Add Savings */}
      <div style={cardS}>
        <div style={hdrS}>🏦 DEPOSIT TO VAULT</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 14, marginBottom: 18 }}>
          <IB label="AMOUNT (₹)" type="number" value={saveAmount} onChange={setSaveAmount} onEnter={handleAddSavings} T={T} />
          <IB label="NOTE (OPTIONAL)" type="text" value={saveNote} onChange={setSaveNote} onEnter={handleAddSavings} placeholder="Emergency Fund, FD, etc." T={T} />
        </div>
        <BtnX onClick={handleAddSavings} disabled={saving} label={saving ? 'PROCESSING...' : 'DEPOSIT SAVINGS'} color={T.amber} />
      </div>

      {/* Deposit History */}
      <div style={cardS}>
        <div style={{ ...hdrS, display: 'flex', justifyContent: 'space-between' }}>
          <span>📋 DEPOSIT HISTORY</span>
          <span style={{ fontSize: 8, color: T.dim, fontWeight: 400 }}>{(data.savingsEntries || []).length} ENTRIES</span>
        </div>
        <div ref={listRef} style={{ maxHeight: 260, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${T.scrollC} transparent` }}>
          {(data.savingsEntries || []).length === 0 && (
            <div style={{ textAlign: 'center', color: T.dim, fontSize: 10, padding: 30, letterSpacing: '0.2em' }}>NO SAVINGS — START YOUR VAULT</div>
          )}
          {[...(data.savingsEntries || [])].reverse().map((entry, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: F }}>
              <div>
                <div style={{ color: T.amber, fontSize: 13, fontWeight: 700 }}>+₹{entry.amount?.toLocaleString('en-IN')}</div>
                <div style={{ color: T.dim, fontSize: 9, marginTop: 3 }}>{entry.note}</div>
              </div>
              <div style={{ color: T.dim, fontSize: 9, textAlign: 'right', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div>
                  {new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  <br />
                  <span style={{ fontSize: 8 }}>{new Date(entry.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div>
                  {deleteConfirm === i ? (
                    <span onClick={() => handleDeleteSavings(i)} style={{ color: T.rose, cursor: 'pointer', fontSize: 8, fontWeight: 900 }}>OK?</span>
                  ) : (
                    <span onClick={() => setDeleteConfirm(i)} style={{ color: T.muted, cursor: 'pointer', fontSize: 11, transition: 'color 0.2s' }}
                      onMouseOver={e => e.currentTarget.style.color = T.rose} onMouseOut={e => e.currentTarget.style.color = T.muted}>✕</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* Sparkline */
const SparkL = ({ data, color, w, h }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data); const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 6) - 3}`).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', width: '100%' }} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="sgold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill="url(#sgold)" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

/* Shared */
const Flash = ({ msg, T }) => (
  <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999, background: msg.startsWith('✓') ? `${T.emerald}18` : `${T.rose}18`, border: `1px solid ${msg.startsWith('✓') ? T.emerald : T.rose}`, color: msg.startsWith('✓') ? T.emerald : T.rose, padding: '10px 28px', borderRadius: 6, fontSize: 11, letterSpacing: '0.2em', fontFamily: F, animation: 'flashIn 0.3s ease' }}>
  {msg}<style>{`@keyframes flashIn { from{opacity:0;transform:translateX(-50%) translateY(-8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
  </div>
);

const SC = ({ label, value, color, T }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: T.isDark ? 'none' : '0 2px 10px rgba(0,0,0,0.05)', transition: 'background .3s' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.5 }} />
    <div style={{ fontSize: 7, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 10 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 900, color, fontFamily: F }}>{value}</div>
  </div>
);

const IB = ({ label, type, value, onChange, onEnter, placeholder, T }) => (
  <div>
    <label style={{ fontSize: 8, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 6, display: 'block' }}>&gt; {label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onEnter?.()} placeholder={placeholder || '0.00'}
      style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, fontSize: 14, letterSpacing: '0.1em', outline: 'none', fontFamily: F, caretColor: T.inputFocus }}
      onFocus={e => e.target.style.borderBottomColor = T.inputFocus} onBlur={e => e.target.style.borderBottomColor = T.inputBdr} />
  </div>
);

const BtnX = ({ onClick, disabled, label, color }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ width: '100%', background: 'transparent', border: `1px solid ${color}44`, color, padding: '12px 0', fontSize: 10, letterSpacing: '0.5em', fontWeight: 700, cursor: 'pointer', fontFamily: F, borderRadius: 6, transition: 'all 0.25s' }}
    onMouseOver={e => { e.currentTarget.style.background = color + '15'; e.currentTarget.style.borderColor = color; }}
    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = color + '44'; }}
  >{label}</button>
);

export default Savings;
